/**
 * P8 Phase C — Onboarding Documents drawer.
 * Used inside OnboardingSubmissions to manage the KYC document upload
 * lifecycle of a single approved submission. Renders the requested-doc
 * matrix, statuses, and approve/reject actions with audit trail.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  FileText, Upload, CheckCircle2, XCircle, Loader2, Download,
  Clock, AlertTriangle, Hash, Link2, RefreshCw, FileArchive, FileBadge,
  CalendarClock,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  pending:  'bg-slate-200 text-slate-700',
  uploaded: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-rose-100 text-rose-800',
  expired:  'bg-orange-100 text-orange-800',
};

const KIND_LABEL = {
  trade_license:       'Trade License',
  trn_certificate:     'TRN Certificate',
  eid:                 'Emirates ID',
  passport:            'Passport',
  cert_incorporation:  'Certificate of Incorporation',
  tax_id:              'Tax ID',
  bank_letter:         'Bank Letter',
  moa:                 'Memorandum of Association',
  ubo_declaration:     'UBO Declaration',
  source_of_funds:     'Source of Funds',
  nda:                 'NDA',
  other:               'Other',
};

const SubmissionDocuments = ({ open, onClose, submission, onChanged }) => {
  const { toast } = useToast();
  const auth = { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } };

  const [docs, setDocs] = useState([]);
  const [portal, setPortal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    if (!submission?.id) return;
    setLoading(true);
    try {
      const r = await axios.get(
        `${API_URL}/api/onboarding/admin/submissions/${submission.id}/documents`, auth,
      );
      setDocs(r.data || []);
    } catch (e) {
      toast({ title: 'Failed to load documents', description: e?.message, variant: 'destructive' });
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submission?.id]);

  const issuePortal = useCallback(async () => {
    try {
      const r = await axios.post(
        `${API_URL}/api/onboarding/admin/submissions/${submission.id}/portal-token`,
        {}, auth,
      );
      setPortal(r.data);
    } catch (e) {
      toast({ title: 'Failed to issue portal token', description: e?.message, variant: 'destructive' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submission?.id]);

  const requestDocs = useCallback(async () => {
    try {
      await axios.post(
        `${API_URL}/api/onboarding/admin/submissions/${submission.id}/documents/request`,
        {}, auth,
      );
      await load();
      toast({ title: 'Document list refreshed' });
    } catch (e) {
      toast({ title: 'Failed to request documents', description: e?.message, variant: 'destructive' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submission?.id, load]);

  useEffect(() => {
    if (open && submission?.id) { load(); setPortal(null); }
  }, [open, submission?.id, load]);

  const approve = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API_URL}/api/onboarding/admin/documents/${id}/approve`, {}, auth);
      await load(); onChanged && onChanged();
      toast({ title: 'Document approved' });
    } catch (e) {
      toast({ title: 'Failed to approve', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setBusyId(null); }
  };

  const submitReject = async () => {
    if (!rejectFor || !rejectReason.trim()) return;
    setBusyId(rejectFor);
    try {
      await axios.post(
        `${API_URL}/api/onboarding/admin/documents/${rejectFor}/reject`,
        { reason: rejectReason.trim() }, auth,
      );
      await load(); onChanged && onChanged();
      setRejectFor(null); setRejectReason('');
      toast({ title: 'Document rejected', description: 'Lead has been notified.' });
    } catch (e) {
      toast({ title: 'Failed to reject', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setBusyId(null); }
  };

  const download = async (id, filename) => {
    try {
      const r = await axios.get(
        `${API_URL}/api/onboarding/admin/documents/${id}/download`,
        { ...auth, responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = filename || `document-${id}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'Download failed', description: e?.message, variant: 'destructive' });
    }
  };

  const downloadPack = async (kind) => {
    try {
      const r = await axios.get(
        `${API_URL}/api/onboarding/admin/submissions/${submission.id}/kyc-pack/${kind}`,
        { ...auth, responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `KYC-Pack-${submission.submission_number || submission.id}.${kind}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: `KYC Pack ${kind.toUpperCase()} downloaded` });
    } catch (e) {
      toast({
        title: `Failed to export ${kind.toUpperCase()}`,
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    }
  };

  const totalRequired = docs.filter(d => d.required).length;
  const approvedCount = docs.filter(d => d.required && d.status === 'approved').length;
  const portalLink = portal
    ? `${window.location.origin}/portal/upload/${portal.token}`
    : null;

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent
          className="max-w-4xl max-h-[92vh] overflow-y-auto"
          data-testid="submission-documents-drawer">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              <FileText className="w-4 h-4 text-[#0F172A]" />
              KYC Documents — {submission?.legal_name || '—'}
              <Badge className="bg-slate-100 text-slate-700 text-[10px] uppercase">
                {approvedCount}/{totalRequired} approved
              </Badge>
            </DialogTitle>
            <DialogDescription className="sr-only">
              Manage required KYC documents, approve or reject uploads, and
              issue a secure portal link to the lead.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap gap-2 mb-3">
            <Button size="sm" variant="outline" onClick={requestDocs}
              data-testid="docs-refresh-list-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh required list
            </Button>
            <Button size="sm" variant="outline" onClick={issuePortal}
              data-testid="docs-issue-portal-btn">
              <Link2 className="w-3.5 h-3.5 mr-1" /> Generate portal link
            </Button>
            <Button size="sm" variant="outline"
              onClick={() => downloadPack('zip')}
              disabled={approvedCount === 0}
              data-testid="docs-export-zip-btn"
              className="text-[#0F172A]">
              <FileArchive className="w-3.5 h-3.5 mr-1" /> Export ZIP
            </Button>
            <Button size="sm" variant="outline"
              onClick={() => downloadPack('pdf')}
              disabled={approvedCount === 0}
              data-testid="docs-export-pdf-btn"
              className="text-[#0F172A]">
              <FileBadge className="w-3.5 h-3.5 mr-1" /> Export PDF
            </Button>
            {portalLink && (
              <div
                data-testid="portal-link-display"
                className="text-xs px-3 py-1.5 rounded-md bg-slate-50 border border-slate-200 font-mono break-all flex-1 min-w-[280px]">
                {portalLink}
              </div>
            )}
          </div>

          {loading ? (
            <div className="py-12 text-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin inline" /> Loading documents…
            </div>
          ) : docs.length === 0 ? (
            <div className="py-10 text-center text-slate-500 text-sm border border-dashed rounded-lg"
              data-testid="docs-empty-state">
              No documents requested yet. Click "Refresh required list" to
              generate the KYC matrix for this submission.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="docs-list">
              {docs.map(d => (
                <li
                  key={d.id}
                  data-testid={`doc-row-${d.kind}`}
                  className="p-3 rounded-lg border border-slate-200 bg-white flex flex-wrap items-start gap-3">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-[#2B2D42]">
                        {KIND_LABEL[d.kind] || d.kind}
                      </span>
                      <Badge className={`${STATUS_BADGE[d.status]} text-[10px] uppercase`}>
                        {d.status}
                      </Badge>
                      {d.required && <Badge className="bg-slate-50 text-slate-500 border text-[9px]">Required</Badge>}
                    </div>
                    {d.filename && (
                      <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                        <span className="font-mono truncate">{d.filename}</span>
                        {typeof d.size_bytes === 'number' && (
                          <span>{Math.round(d.size_bytes / 1024)} KB</span>
                        )}
                        {d.sha256 && (
                          <span className="flex items-center gap-1">
                            <Hash className="w-3 h-3" /> {d.sha256.slice(0, 12)}…
                          </span>
                        )}
                      </div>
                    )}
                    {d.uploaded_at && (
                      <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Uploaded {(d.uploaded_at || '').slice(0, 19).replace('T', ' ')}
                        {d.uploaded_by_email ? ` by ${d.uploaded_by_email}` : ''}
                      </div>
                    )}
                    {d.expires_at && (d.status === 'approved' || d.status === 'expired') && (
                      <div
                        data-testid={`doc-expiry-${d.kind}`}
                        className={`text-[11px] mt-0.5 flex items-center gap-1 ${
                          (() => {
                            const ms = new Date(d.expires_at) - new Date();
                            const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
                            if (days < 0) return 'text-rose-600 font-medium';
                            if (days <= 60) return 'text-orange-600';
                            return 'text-slate-400';
                          })()
                        }`}>
                        <CalendarClock className="w-3 h-3" />
                        {(() => {
                          const ms = new Date(d.expires_at) - new Date();
                          const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
                          if (days < 0) return `Expired ${Math.abs(days)}d ago — re-upload required`;
                          if (days <= 60) return `Expires in ${days}d — renewal soon`;
                          return `Valid until ${(d.expires_at || '').slice(0, 10)}`;
                        })()}
                      </div>
                    )}
                    {d.rejection_reason && (
                      <div className="mt-1.5 text-xs px-2 py-1 rounded bg-rose-50 border border-rose-200 text-rose-700 inline-block">
                        <AlertTriangle className="w-3 h-3 inline mr-1" />
                        {d.rejection_reason}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {d.status === 'uploaded' && (
                      <>
                        <Button size="sm" variant="outline"
                          onClick={() => download(d.id, d.filename)}
                          data-testid={`doc-download-${d.kind}`}>
                          <Download className="w-3 h-3 mr-1" /> Download
                        </Button>
                        <Button size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          disabled={busyId === d.id}
                          onClick={() => approve(d.id)}
                          data-testid={`doc-approve-${d.kind}`}>
                          {busyId === d.id ? <Loader2 className="w-3 h-3 animate-spin" /> :
                            <><CheckCircle2 className="w-3 h-3 mr-1" /> Approve</>}
                        </Button>
                        <Button size="sm" variant="outline"
                          className="text-rose-600 border-rose-200 hover:bg-rose-50"
                          disabled={busyId === d.id}
                          onClick={() => { setRejectFor(d.id); setRejectReason(''); }}
                          data-testid={`doc-reject-${d.kind}`}>
                          <XCircle className="w-3 h-3 mr-1" /> Reject
                        </Button>
                      </>
                    )}
                    {d.status === 'approved' && (
                      <Button size="sm" variant="outline"
                        onClick={() => download(d.id, d.filename)}
                        data-testid={`doc-download-${d.kind}`}>
                        <Download className="w-3 h-3 mr-1" /> Download
                      </Button>
                    )}
                    {d.status === 'pending' && (
                      <span className="text-xs text-slate-400 italic">
                        Awaiting upload from lead
                      </span>
                    )}
                    {d.status === 'rejected' && (
                      <span className="text-xs text-rose-600 italic">
                        Awaiting re-upload
                      </span>
                    )}
                    {d.status === 'expired' && (
                      <span className="text-xs text-orange-600 italic">
                        Expired — renewal required
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={onClose} data-testid="docs-close-btn">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject reason dialog */}
      <Dialog open={!!rejectFor} onOpenChange={() => setRejectFor(null)}>
        <DialogContent data-testid="doc-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject document</DialogTitle>
            <DialogDescription className="sr-only">
              Provide a clear reason. The lead will be notified by email and
              must re-upload the document.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Reason (visible to lead — be specific)…"
            rows={4}
            data-testid="doc-reject-reason-input" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectFor(null)}>Cancel</Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700"
              disabled={!rejectReason.trim() || busyId === rejectFor}
              onClick={submitReject}
              data-testid="doc-reject-confirm-btn">
              {busyId === rejectFor ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm rejection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export { Upload };
export default SubmissionDocuments;
